import React, { useEffect, useState } from "react"
import { View } from "react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import ConstraintsGridStep from "@common/student-diagnostic-stepper/ConstraintsGridStep"
import { gridDisplayOffsetHours } from "@common/student-diagnostic-stepper/constraintsGrid"

/**
 * Choix des créneaux, en modale depuis l'étape de configuration.
 *
 * La grille elle-même n'est PAS réécrite : c'est {@link ConstraintsGridStep},
 * le composant déjà servi par `subscribe/possible-slots`, monté tel quel. On
 * n'apporte que l'enveloppe (modale + validation), pour que le parent règle
 * tout son accompagnement depuis un seul écran sans que celui-ci déborde.
 *
 * La sélection ne remonte qu'à la VALIDATION : un parent qui ferme la modale
 * en croix retrouve ses créneaux précédents, pas un état à moitié coché.
 *
 * Ces créneaux sont des DISPONIBILITÉS, pas des heures achetées. Ça n'a pas
 * toujours été vrai : le volume se déduisait du nombre de cases cochées, si
 * bien qu'un seul contrôle répondait à deux questions — combien j'achète, et
 * quand mon enfant est libre. Il fallait alors écrire l'inverse de ce qu'on
 * voulait vraiment dire, puisque chaque case coûtait de l'argent.
 *
 * Le volume étant désormais saisi à part, on peut demander franchement ce dont
 * l'attribution a besoin : au moins autant de créneaux que d'heures achetées,
 * et davantage si possible. Plus aucun plafond — cocher une case de plus ne
 * coûte rien et augmente les chances de bien placer l'élève.
 */
const SlotsPickerModal = ({
    isVisible,
    onClose,
    onValidate,
    initialSlots = [],
    // Le dimanche n'est ouvert qu'en cours particulier : en partagé, un groupe
    // ne se staffe pas le dimanche.
    includeSunday = false,
    minSlots = 1,
    // Volume ACHETÉ, pour dire au parent pourquoi on lui demande au moins
    // autant de créneaux. Les créneaux eux-mêmes ne sont plus plafonnés : ce
    // sont des disponibilités, cocher une case ne coûte plus rien.
    weeklyHours = 0,
    userTimezone = null,
    serverTimezone = null,
    // Date à laquelle les créneaux seront tenus, pour mesurer l'écart de fuseau
    // LÀ plutôt qu'aujourd'hui : Paris change d'heure, Alger non, donc une
    // grille de septembre ouverte en janvier s'afficherait décalée d'une heure.
    // Absente = mesure au render, ce qui est juste quand la grille sert un
    // démarrage imminent (funnel d'inscription).
    at = null,
}) => {
    const [selected, setSelected] = useState(initialSlots)

    // La grille énonce l'OFFRE : ses cases nomment des heures de Paris, et ce
    // sont elles qu'on envoie. Elles s'AFFICHENT en revanche à l'heure de la
    // famille — comme tout le reste de l'app — pour qu'un parent à l'étranger
    // ne réserve pas une heure à côté de ce qu'il croit.
    const displayOffsetHours = gridDisplayOffsetHours(
        userTimezone,
        serverTimezone,
        at
    )

    // Ré-ouvrir la modale doit toujours repartir de ce qui est réellement
    // enregistré — pas de la dernière sélection abandonnée.
    useEffect(() => {
        if (isVisible) setSelected(initialSlots)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible])

    const count = selected.length
    const enough = count >= minSlots

    return (
        <ModalSkeleton
            isVisible={isVisible}
            onClose={onClose}
            title="Vos créneaux"
            scrollable={false}
        >
            <TextM style={styles.helper} wrap>
                {weeklyHours
                    ? `${weeklyHours} h par semaine choisies : cochez au moins ${minSlots} créneau${minSlots > 1 ? "x" : ""} — plus de disponibilités, meilleur placement.`
                    : "Cochez toutes les disponibilités de votre enfant : plus vous nous en donnez, plus il est facile de le placer avec le bon masteur."}
            </TextM>

            {displayOffsetHours !== 0 ? (
                <TextM style={styles.timezoneNotice} wrap>
                    Horaires affichés à votre heure locale.
                </TextM>
            ) : null}

            <View style={styles.grid}>
                <ConstraintsGridStep
                    compact
                    includeSunday={includeSunday}
                    displayOffsetHours={displayOffsetHours}
                    field={{ value: selected, onChange: setSelected }}
                />
            </View>

            <View style={styles.footer}>
                <TextM style={styles.count} wrap>
                    {count === 0
                        ? "Aucun créneau sélectionné"
                        : `${count} créneau${count > 1 ? "x" : ""} donné${count > 1 ? "s" : ""}`}
                </TextM>
                {/* Le POURQUOI du plancher se lit au-dessus du bouton : dans le
                    libellé, il débordait et se faisait tronquer par l'ellipse. */}
                {enough && weeklyHours > 0 && count > weeklyHours ? (
                    <TextM style={styles.capNotice} wrap>
                        {weeklyHours} h choisies : les autres créneaux nous
                        servent à trouver le meilleur placement.
                    </TextM>
                ) : null}
                <FilledButtonM
                    label={
                        enough
                            ? "Valider mes créneaux"
                            : `Sélectionnez ${minSlots} créneau${minSlots > 1 ? "x" : ""}`
                    }
                    onPress={() => onValidate(selected)}
                    disabled={!enough}
                    color={
                        enough ? theme.colors.primary : theme.colors.lightMore
                    }
                    style={{ width: "100%", marginBottom: 0 }}
                />
            </View>
        </ModalSkeleton>
    )
}

const styles = {
    helper: {
        fontSize: 13,
        color: theme.colors.grey,
        marginBottom: 12,
    },
    // Hauteur bornée : la grille possède son propre scroll (d'où
    // `scrollable={false}` sur la modale), l'en-tête et le pied restent fixes.
    grid: { maxHeight: 360 },
    footer: { paddingTop: 12, gap: 8 },
    count: {
        fontSize: 13,
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
        textAlign: "center",
    },
    capNotice: {
        fontSize: 12,
        color: theme.colors.grey,
        textAlign: "center",
    },
    timezoneNotice: {
        fontSize: 12,
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
        marginBottom: 8,
    },
}

export default SlotsPickerModal
